import type { RoomTag } from "./room-metadata";

/** Source room ids used on the /book ladder (matches `ROOM_LADDER` entries). */
export type BookRoomLadderId =
  | "junior-suite"
  | "family-suite"
  | "superior-double-room"
  | "double-twin-balcony-lake"
  | "double-twin-balcony-village"
  | "double-twin-noBal"
  | "standard-triple-room"
  | "single-balcony-lake"
  | "heritage-single"
  | "basic-single";

export type BookRoomLadderCopy = Record<
  BookRoomLadderId,
  { displayName: string; description: string }
>;

export const bookRoomLadderCopyEn: BookRoomLadderCopy = {
  "junior-suite": {
    displayName: "Alpine Junior Suite - Panorama View",
    description: "Separate living area with sophisticated luxury.",
  },
  "family-suite": {
    displayName: "Heritage Family Suite - Jungfrau View",
    description: "Home-away-from-home with kitchenette & panoramic views.",
  },
  "superior-double-room": {
    displayName: "Jungfrau Superior Double - Mountain & Lake View",
    description: "Elevated experience with premium amenities.",
  },
  "double-twin-balcony-lake": {
    displayName: "Alpine Double/Twin - Mountain & Lake View Balcony",
    description: "Wake up to iconic Jungfrau views from your balcony.",
  },
  "double-twin-balcony-village": {
    displayName: "Alpine Double/Twin - Village View Balcony",
    description: "Classic comfort with a charming village balcony.",
  },
  "double-twin-noBal": {
    displayName: "Classic Double/Twin",
    description: "Comfortable retreat after a day of adventure.",
  },
  "standard-triple-room": {
    displayName: "Classic Triple Room - Alpine Comfort",
    description: "Spacious & flexible for small groups.",
  },
  "single-balcony-lake": {
    displayName: "Jungfrau Single - Mountain & Lake View Balcony",
    description: "Your private balcony with majestic views.",
  },
  "heritage-single": {
    displayName: "Heritage Single - Village View",
    description: "Extra space & comfort for solo stays.",
  },
  "basic-single": {
    displayName: "Classic Single Room - Alpine Comfort",
    description: "Essential comfort for the active traveler.",
  },
};

export const bookRoomLadderCopyDe: BookRoomLadderCopy = {
  "junior-suite": {
    displayName: "Alpine Junior Suite – Panoramablick",
    description: "Separater Wohnbereich mit raffinierter Ausstattung.",
  },
  "family-suite": {
    displayName: "Heritage Familiensuite – Jungfraublick",
    description: "Wie zu Hause – mit Kochnische und Panoramablick.",
  },
  "superior-double-room": {
    displayName: "Jungfrau Superior Doppelzimmer – Berg- & Seeblick",
    description: "Gehobener Komfort mit erstklassiger Ausstattung.",
  },
  "double-twin-balcony-lake": {
    displayName: "Alpines Doppel-/Zweibettzimmer – Berg- & Seeblick mit Balkon",
    description: "Erwachen Sie mit ikonischem Jungfrau-Panorama auf Ihrem Balkon.",
  },
  "double-twin-balcony-village": {
    displayName: "Alpines Doppel-/Zweibettzimmer – Dorfblick mit Balkon",
    description: "Klassischer Komfort mit charmantem Dorfbalkon.",
  },
  "double-twin-noBal": {
    displayName: "Klassisches Doppel-/Zweibettzimmer",
    description: "Behaglicher Rückzugsort nach einem Tag in den Bergen.",
  },
  "standard-triple-room": {
    displayName: "Klassisches Dreibettzimmer – Alpiner Komfort",
    description: "Geräumig und flexibel für kleine Gruppen.",
  },
  "single-balcony-lake": {
    displayName: "Jungfrau Einzelzimmer – Berg- & Seeblick mit Balkon",
    description: "Ihr privater Balkon mit majestätischem Ausblick.",
  },
  "heritage-single": {
    displayName: "Heritage Einzelzimmer – Dorfblick",
    description: "Mehr Raum und Komfort für Alleinreisende.",
  },
  "basic-single": {
    displayName: "Klassisches Einzelzimmer – Alpiner Komfort",
    description: "Solider Komfort für aktive Gäste.",
  },
};

export const bookRoomLadderCopyFr: BookRoomLadderCopy = {
  "junior-suite": {
    displayName: "Junior suite alpine – vue panoramique",
    description: "Coin salon séparé et confort raffiné.",
  },
  "family-suite": {
    displayName: "Suite familiale Heritage – vue Jungfrau",
    description: "Comme à la maison, avec kitchenette et vues panoramiques.",
  },
  "superior-double-room": {
    displayName: "Chambre double supérieure Jungfrau – montagne & lac",
    description: "Expérience haut de gamme avec équipements premium.",
  },
  "double-twin-balcony-lake": {
    displayName: "Double/Twin alpine – montagne & lac avec balcon",
    description: "Réveillez-vous face à l’emblème de la Jungfrau depuis votre balcon.",
  },
  "double-twin-balcony-village": {
    displayName: "Double/Twin alpine – vue village avec balcon",
    description: "Confort classique avec un balcon plein de charme sur le village.",
  },
  "double-twin-noBal": {
    displayName: "Double/Twin classique",
    description: "Cocon confortable après une journée d’aventure.",
  },
  "standard-triple-room": {
    displayName: "Chambre triple classique – confort alpin",
    description: "Spacieuse et flexible pour les petits groupes.",
  },
  "single-balcony-lake": {
    displayName: "Chambre simple Jungfrau – montagne & lac avec balcon",
    description: "Votre balcon privé avec vue majestueuse.",
  },
  "heritage-single": {
    displayName: "Chambre simple Heritage – vue village",
    description: "Plus d’espace et de confort pour les séjours en solo.",
  },
  "basic-single": {
    displayName: "Chambre simple classique – confort alpin",
    description: "L’essentiel pour les voyageurs actifs.",
  },
};

export type BookUpsellCopyId =
  | "massage-express-25"
  | "massage-relaxation-50"
  | "massage-pamper-75"
  | "sauna-full"
  | "sauna-heat"
  | "sauna-steam-ice"
  | "dinner-5-course"
  | "romantic-setup";

export type BookUpsellCopy = Record<BookUpsellCopyId, { name: string; description: string }>;

export const bookUpsellsCopyEn: BookUpsellCopy = {
  "massage-express-25": {
    name: "Alpine Wellness Taster (25 min)",
    description:
      "Short NutraSwiss spa session when time is tight. On arrival, tell us your focus and we'll match the treatment.",
  },
  "massage-relaxation-50": {
    name: "Relaxation Package (50 min)",
    description:
      "One 50-minute NutraSwiss treatment. Our team helps you choose on arrival—unwind, ease muscles, or focus on back and feet.",
  },
  "massage-pamper-75": {
    name: "Pamper Package (75 min)",
    description:
      "75 minutes at our NutraSwiss spa. At check-in we help you pick deep relaxation, recovery, or a nourishing body treatment.",
  },
  "sauna-full": {
    name: "Sauna & Spa Day Package",
    description: "Full thermal area + complimentary drinks. Per person.",
  },
  "sauna-heat": {
    name: "Heat Experience (Finnish & Bio Sauna)",
    description: "Day access to Finnish and Bio sauna.",
  },
  "sauna-steam-ice": {
    name: "Steam & Ice Experience",
    description: "Day access to steam bath and ice plunge.",
  },
  "dinner-5-course": {
    name: "5-Course Dinner at Restaurant Jungfrau",
    description: "5-course menu; select main on arrival. Per person per night.",
  },
  "romantic-setup": {
    name: "Romantic Decoration",
    description: "Chocolate and Prosecco in room on arrival.",
  },
};

export const bookUpsellsCopyDe: BookUpsellCopy = {
  "massage-express-25": {
    name: "Alpine Wellness Schnupper (25 Min.)",
    description:
      "Kurze NutraSwiss-Spa-Zeit bei wenig Zeit. Bei Ankunft nennen Sie uns Ihren Fokus – wir empfehlen die passende Behandlung.",
  },
  "massage-relaxation-50": {
    name: "Entspannungspaket (50 Min.)",
    description:
      "50 Min. NutraSwiss-Behandlung. Unser Team hilft bei der Ankunft bei der Wahl: Entspannung, Muskeln oder Rücken und Füsse.",
  },
  "massage-pamper-75": {
    name: "Wellness-Paket (75 Min.)",
    description:
      "75 Min. in der NutraSwiss Spa. Beim Check-in wählen wir gemeinsam: tiefe Entspannung, Regeneration oder pflegende Körperbehandlung.",
  },
  "sauna-full": {
    name: "Sauna- & Spa-Tagespaket",
    description: "Zugang zum gesamten Thermalbereich inkl. Erfrischungen. Pro Person.",
  },
  "sauna-heat": {
    name: "Hitze-Erlebnis (Finnische & Bio-Sauna)",
    description: "Tageszugang zur finnischen und Bio-Sauna.",
  },
  "sauna-steam-ice": {
    name: "Dampf- & Eis-Erlebnis",
    description: "Tageszugang zu Dampfbad und Eisgrotte/-tauchbecken.",
  },
  "dinner-5-course": {
    name: "5-Gang-Menü im Restaurant Jungfrau",
    description: "5-Gang-Menü; Hauptgang bei Ankunft wählen. Pro Person und Nacht.",
  },
  "romantic-setup": {
    name: "Romantische Dekoration",
    description: "Schokolade und Prosecco bei Ankunft auf dem Zimmer.",
  },
};

export const bookUpsellsCopyFr: BookUpsellCopy = {
  "massage-express-25": {
    name: "Découverte bien-être alpine (25 min)",
    description:
      "Court soin au spa NutraSwiss si vous manquez de temps. À l’arrivée, dites-nous votre besoin et nous adaptons le soin.",
  },
  "massage-relaxation-50": {
    name: "Formule détente (50 min)",
    description:
      "Soin de 50 min au spa NutraSwiss. À l’arrivée, l’équipe vous aide : détente, muscles ou dos et pieds.",
  },
  "massage-pamper-75": {
    name: "Formule cocooning (75 min)",
    description:
      "75 min au spa NutraSwiss. Au check-in, nous vous aidons : relaxation profonde, récupération ou soin corps nourrissant.",
  },
  "sauna-full": {
    name: "Forfait journée sauna & spa",
    description: "Accès à tout l’espace thermal + boissons offertes. Par personne.",
  },
  "sauna-heat": {
    name: "Expérience chaleur (sauna finlandaise & bio)",
    description: "Accès journée aux saunas finlandais et bio.",
  },
  "sauna-steam-ice": {
    name: "Expérience vapeur & glace",
    description: "Accès journée au bain de vapeur et au bain froid / plongeon glacé.",
  },
  "dinner-5-course": {
    name: "Dîner 5 services au Restaurant Jungfrau",
    description: "Menu 5 services ; plat principal choisi à l’arrivée. Par personne et par nuit.",
  },
  "romantic-setup": {
    name: "Décoration romantique",
    description: "Chocolat et Prosecco dans la chambre à l’arrivée.",
  },
};

export const bookRoomTagsEn: Record<RoomTag, string> = {
  "ULTIMATE LUXURY": "ULTIMATE LUXURY",
  "RARE FIND": "RARE FIND",
  RECOMMENDED: "RECOMMENDED",
  "GUEST FAVORITE": "GUEST FAVORITE",
  "VALUE CHOICE": "VALUE CHOICE",
  "FAMILY FRIENDLY": "FAMILY FRIENDLY",
  CLASSIC: "CLASSIC",
  "SOLO FAVORITE": "SOLO FAVORITE",
  ENHANCED: "ENHANCED",
  BUDGET: "BUDGET",
  "SPACE FOR ALL": "SPACE FOR ALL",
};

export const bookRoomTagsDe: Record<RoomTag, string> = {
  "ULTIMATE LUXURY": "ULTIMATIVER LUXUS",
  "RARE FIND": "SELTENHEIT",
  RECOMMENDED: "EMPFOHLEN",
  "GUEST FAVORITE": "GÄSTEFAVORIT",
  "VALUE CHOICE": "TOP PREIS",
  "FAMILY FRIENDLY": "FAMILIENFREUNDLICH",
  CLASSIC: "KLASSISCH",
  "SOLO FAVORITE": "SOLO-TIPP",
  ENHANCED: "KOMFORT PLUS",
  BUDGET: "GÜNSTIG",
  "SPACE FOR ALL": "PLATZ FÜR ALLE",
};

export const bookRoomTagsFr: Record<RoomTag, string> = {
  "ULTIMATE LUXURY": "LUXE ABSOLU",
  "RARE FIND": "RARETÉ",
  RECOMMENDED: "RECOMMANDÉ",
  "GUEST FAVORITE": "COUP DE CŒUR",
  "VALUE CHOICE": "BON RAPPORT QUALITÉ",
  "FAMILY FRIENDLY": "FAMILIAL",
  CLASSIC: "CLASSIQUE",
  "SOLO FAVORITE": "COUP DE CŒUR SOLO",
  ENHANCED: "CONFORT RENFORCÉ",
  BUDGET: "ÉCONOMIQUE",
  "SPACE FOR ALL": "GRAND ESPACE",
};
